import { gql, FetchGraphql } from '@cityofboston/next-client-common';
import Cart, {
  DEATH_RELATIONSHIP_OPTIONS,
} from '../store/DeathCertificateCart';
import Order from '../models/Order';

import {
  SubmitDeathCertificateOrder,
  SubmitDeathCertificateOrderVariables,
} from './graphql-types';
import { DeathCertificateOrderResult } from '../types';

const QUERY = gql`
  mutation SubmitDeathCertificateOrder(
    $contactName: String!
    $contactEmail: String!
    $confirmContactEmail: String!
    $contactPhone: String!
    $shippingName: String!
    $shippingCompanyName: String!
    $shippingAddress1: String!
    $shippingAddress2: String!
    $shippingCity: String!
    $shippingState: String!
    $shippingZip: String!
    $cardToken: String!
    $cardLast4: String!
    $cardholderName: String!
    $billingAddress1: String!
    $billingAddress2: String!
    $billingCity: String!
    $billingState: String!
    $billingZip: String!
    $items: [DeathCertificateOrderItemInput!]!
    $tracking: Boolean!
    $idempotencyKey: String!
  ) {
    submitDeathCertificateOrder(
      contactName: $contactName
      contactEmail: $contactEmail
      confirmContactEmail: $confirmContactEmail
      contactPhone: $contactPhone
      shippingName: $shippingName
      shippingCompanyName: $shippingCompanyName
      shippingAddress1: $shippingAddress1
      shippingAddress2: $shippingAddress2
      shippingCity: $shippingCity
      shippingState: $shippingState
      shippingZip: $shippingZip
      cardToken: $cardToken
      cardLast4: $cardLast4
      cardholderName: $cardholderName
      billingAddress1: $billingAddress1
      billingAddress2: $billingAddress2
      billingCity: $billingCity
      billingState: $billingState
      billingZip: $billingZip
      items: $items
      tracking: $tracking
      idempotencyKey: $idempotencyKey
    ) {
      order {
        id
      }
      error {
        message
        cause
      }
    }
  }
`;

export default async function submitDeathCertificateOrder(
  fetchGraphql: FetchGraphql,
  cart: Cart,
  order: Order,
  tracking: boolean
): Promise<DeathCertificateOrderResult> {
  const {
    info: {
      contactName,
      contactEmail,
      confirmContactEmail,
      contactPhone,
      shippingName,
      shippingCompanyName,
      shippingAddress1,
      shippingAddress2,
      shippingCity,
      shippingState,
      shippingZip,
      cardholderName,
      cardLast4,
      cardToken,
    },
    billingAddress1,
    billingAddress2,
    billingCity,
    billingState,
    billingZip,
    idempotencyKey,
  } = order;

  if (!cardToken || !cardLast4) {
    throw new Error(
      'submitDeathCertificateOrder called before card tokenization'
    );
  }

  if (!idempotencyKey) {
    throw new Error(
      'submitDeathCertificateOrder called before setting idempotencyKey'
    );
  }

  const queryVariables: SubmitDeathCertificateOrderVariables = {
    contactName,
    contactEmail,
    confirmContactEmail,
    contactPhone,
    shippingName,
    shippingCompanyName,
    shippingAddress1,
    shippingAddress2,
    shippingCity,
    shippingState,
    shippingZip,
    cardholderName,
    cardToken,
    cardLast4,
    billingAddress1,
    billingAddress2,
    billingState,
    billingCity,
    billingZip,
    items: cart.entries.map(e => {
      const includeSsn = !!e.includeSsn;
      const relationshipLabel =
        e.relationship && DEATH_RELATIONSHIP_OPTIONS[e.relationship]
          ? DEATH_RELATIONSHIP_OPTIONS[e.relationship].label
          : e.relationship || null;

      return {
        id: e.id,
        quantity: e.quantity,
        name: e.cert
          ? `${e.cert.firstName} ${e.cert.lastName}`
          : 'Unknown Name',
        includeSsn,
        requesterRelationship: includeSsn ? relationshipLabel : null,
        uploadSessionId:
          includeSsn && e.uploadSessionId ? e.uploadSessionId : null,
      };
    }),
    tracking,
    idempotencyKey,
  };

  const response: SubmitDeathCertificateOrder = await fetchGraphql(
    QUERY,
    queryVariables
  );

  return response.submitDeathCertificateOrder;
}
